import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { ClientInfo } from 'wflow-cdk';
import { ProductEventTrackingService } from 'wflow-features/product-analytics';
import { BreadcrumbPosition, BreadcrumbsModule } from 'wflow-ui';
import { IsDemoComponent } from './is-demo.component';
import { PageTitleComponent } from './page-title.component';
import { TopNavigationSlotsService } from './top-navigation-slots.service';

/** Document-detail URLs — `…/documents/list/kind/<kind>/<type>/detail/<id>`. */
const DOCUMENT_DETAIL_ROUTE = /\/documents\/list\/.*\/detail\//;

@Component({
  selector: 'top-navigation',
  imports: [
    NgTemplateOutlet,
    PageTitleComponent,
    BreadcrumbsModule,
    IsDemoComponent,
  ],
  template: `
    @if (startSlot(); as slot) {
      <div class="top-navigation__group top-navigation__group--start">
        <ng-container
          *ngTemplateOutlet="slot.template; injector: slot.injector"
        />
      </div>
    }

    <div class="top-navigation__group top-navigation__group--center">
      <wf-page-title [isMobile]="isMobile()" />
      @if (!isMobile()) {
        <breadcrumbs (itemClick)="trackBreadcrumbClick($event)" />
      }
    </div>

    <div class="top-navigation__group top-navigation__group--end">
      @if (endSlot(); as slot) {
        <ng-container
          *ngTemplateOutlet="slot.template; injector: slot.injector"
        />
      }

      <is-demo />
    </div>
  `,
  styles: [
    `
      .top-navigation {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: var(--wf-spacer-lg);

        position: relative;
        top: 0;
        left: 0;
        right: 0;
        height: var(--wf-top-navigation-height);
        z-index: 1;

        padding: var(--wf-spacer-sm) var(--wf-spacer-lg);
        border-bottom: 1px solid var(--wf-border-quaternary);

        @media (max-width: 899px) {
          justify-content: flex-start;
          gap: var(--wf-spacer-lg);
        }
      }

      .top-navigation__group {
        display: flex;
        gap: var(--wf-spacer-lg);
        align-items: center;

        &,
        &--start {
          justify-content: flex-start;
        }

        &--center {
          align-items: baseline;
        }

        &--end {
          justify-content: flex-end;
          justify-self: end;

          @media (max-width: 899px) {
            margin-left: auto;
          }
        }

        @media (max-width: 899px) {
          gap: 0px;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'top-navigation',
  },
})
export class TopNavigationComponent {
  private readonly slotsSvc = inject(TopNavigationSlotsService);
  private readonly router = inject(Router);
  private readonly productTrackingSvc = inject(ProductEventTrackingService);
  protected readonly startSlot = this.slotsSvc.startSlot;
  protected readonly endSlot = this.slotsSvc.endSlot;
  protected readonly isMobile = inject(ClientInfo).isMobile;

  /**
   * Area E P2 [Frequency] — breadcrumb used to navigate away from a document
   * detail. The trail is app-shell UI shown on every page, so the push is gated
   * on the document-detail route to keep the event `document_detail`-scoped; the
   * value carries which part of the trail was clicked, never a name.
   */
  protected trackBreadcrumbClick(position: BreadcrumbPosition): void {
    if (!DOCUMENT_DETAIL_ROUTE.test(this.router.url)) return;

    this.productTrackingSvc.track('detail_header_breadcrumb_click', position);
  }
}
